import { supabase } from '../lib/supabase';

const createItem = async (itemData) => {
  const { data, error } = await supabase?.from('inventory_items')?.insert([itemData])?.select()?.single();

  if (error) throw error;
  return data;
};

const updateItem = async (id, itemData) => {
  const { data, error } = await supabase?.from('inventory_items')?.update(itemData)?.eq('id', id)?.select()?.single();

  if (error) throw error;
  return data;
};

const deleteItem = async (id) => {
  const { error } = await supabase?.from('inventory_items')?.delete()?.eq('id', id);

  if (error) throw error;
};

const createSupplier = async (supplierData) => {
  const { data, error } = await supabase?.from('suppliers')?.insert([supplierData])?.select()?.single();

  if (error) throw error;
  return data;
};

const updateSupplier = async (id, supplierData) => {
  const { data, error } = await supabase?.from('suppliers')?.update(supplierData)?.eq('id', id)?.select()?.single();

  if (error) throw error;
  return data;
};

const deleteSupplier = async (id) => {
  const { error } = await supabase?.from('suppliers')?.delete()?.eq('id', id);

  if (error) throw error;
};

export const inventoryService = {
  // Get all inventory items with supplier info
  async getInventoryItems(filters = {}) {
    try {
      let query = supabase?.from('inventory_items')?.select(`
          *,
          supplier:suppliers(id, name, contact_person, phone, email)
        `)?.order('created_at', { ascending: false });

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.alertLevel) {
        query = query?.eq('alert_level', filters?.alertLevel);
      }

      if (filters?.search) {
        query = query?.or(`name.ilike.%${filters?.search}%,item_code.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(item => ({
        id: item?.id,
        itemCode: item?.item_code,
        itemName: item?.name,
        category: item?.category,
        description: item?.description,
        currentQuantity: item?.current_quantity,
        minimumQuantity: item?.minimum_quantity,
        maximumQuantity: item?.maximum_quantity,
        unitOfMeasure: item?.unit_of_measure,
        alertLevel: item?.alert_level,
        alertEnabled: item?.alert_enabled,
        storageLocation: item?.storage_location,
        warehouseSection: item?.warehouse_section,
        unitCost: item?.unit_cost,
        currency: item?.currency,
        preferredSupplierId: item?.preferred_supplier_id,
        supplier: item?.supplier ? {
          id: item?.supplier?.id,
          companyName: item?.supplier?.name,
          contactPerson: item?.supplier?.contact_person,
          phone: item?.supplier?.phone,
          email: item?.supplier?.email,
        } : null,
        image: item?.image,
        notes: item?.notes,
        metadata: item?.metadata,
        createdBy: item?.created_by,
        createdAt: item?.created_at,
        updatedAt: item?.updated_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      throw error;
    }
  },

  // Get low stock alerts
  async getLowStockAlerts() {
    try {
      const { data, error } = await supabase?.from('inventory_items')?.select(`
          *,
          supplier:suppliers(name, contact_person, phone, email)
        `)?.not('alert_level', 'is', null)?.eq('alert_enabled', true)?.order('alert_level', { ascending: false });

      if (error) throw error;

      return data?.map(item => ({
        id: item?.id,
        itemCode: item?.item_code,
        itemName: item?.name,
        currentQuantity: item?.current_quantity,
        minimumQuantity: item?.minimum_quantity,
        alertLevel: item?.alert_level,
        storageLocation: item?.storage_location,
        supplier: item?.supplier,
      })) || [];
    } catch (error) {
      console.error('Error fetching low stock alerts:', error);
      throw error;
    }
  },

  // Create inventory item
  async createInventoryItem(itemData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { data: itemCodeData } = await supabase?.rpc('generate_item_code');

      const { data, error } = await supabase?.from('inventory_items')?.insert([{
          item_code: itemCodeData,
          name: itemData?.itemName,
          category: itemData?.category,
          description: itemData?.description,
          current_quantity: itemData?.currentQuantity || 0,
          minimum_quantity: itemData?.minimumQuantity || 10,
          maximum_quantity: itemData?.maximumQuantity,
          unit_of_measure: itemData?.unitOfMeasure || 'unit',
          alert_enabled: itemData?.alertEnabled !== false,
          storage_location: itemData?.storageLocation,
          warehouse_section: itemData?.warehouseSection,
          unit_cost: itemData?.unitCost,
          currency: itemData?.currency || 'USD',
          preferred_supplier_id: itemData?.preferredSupplierId,
          image: itemData?.image,
          notes: itemData?.notes,
          metadata: itemData?.metadata,
          created_by: user?.id,
        }])?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating inventory item:', error);
      throw error;
    }
  },

  // Update inventory item
  async updateInventoryItem(itemId, updates) {
    try {
      const { data, error } = await supabase?.from('inventory_items')?.update({
          name: updates?.itemName,
          category: updates?.category,
          description: updates?.description,
          current_quantity: updates?.currentQuantity,
          minimum_quantity: updates?.minimumQuantity,
          maximum_quantity: updates?.maximumQuantity,
          unit_of_measure: updates?.unitOfMeasure,
          alert_enabled: updates?.alertEnabled,
          storage_location: updates?.storageLocation,
          warehouse_section: updates?.warehouseSection,
          unit_cost: updates?.unitCost,
          currency: updates?.currency,
          preferred_supplier_id: updates?.preferredSupplierId,
          image: updates?.image,
          notes: updates?.notes,
          metadata: updates?.metadata,
        })?.eq('id', itemId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw error;
    }
  },

  // Adjust stock quantity
  async adjustStock(itemId, quantity, movementType, reason, referenceType = null, referenceId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      // Get current item
      const { data: item, error: itemError } = await supabase?.from('inventory_items')?.select('current_quantity')?.eq('id', itemId)?.single();

      if (itemError) throw itemError;

      const previousQuantity = item?.current_quantity;
      const newQuantity = movementType === 'in' 
        ? previousQuantity + quantity 
        : previousQuantity - quantity;

      // Update inventory
      const { error: updateError } = await supabase?.from('inventory_items')?.update({ current_quantity: newQuantity })?.eq('id', itemId);

      if (updateError) throw updateError;

      // Log movement
      const { error: movementError } = await supabase?.from('stock_movements')?.insert([{
          item_id: itemId,
          movement_type: movementType,
          quantity: quantity,
          previous_quantity: previousQuantity,
          new_quantity: newQuantity,
          reference_type: referenceType,
          reference_id: referenceId,
          reason: reason,
          performed_by: user?.id,
        }]);

      if (movementError) throw movementError;

      return { success: true, newQuantity };
    } catch (error) {
      console.error('Error adjusting stock:', error);
      throw error;
    }
  },

  // Get stock movements
  async getStockMovements(itemId = null, limit = 50) {
    try {
      let query = supabase?.from('stock_movements')?.select(`
          *,
          item:inventory_items(item_code, name),
          performer:user_profiles(full_name)
        `)?.order('created_at', { ascending: false })?.limit(limit);

      if (itemId) {
        query = query?.eq('item_id', itemId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(movement => ({
        id: movement?.id,
        itemId: movement?.item_id,
        item: movement?.item,
        movementType: movement?.movement_type,
        quantity: movement?.quantity,
        previousQuantity: movement?.previous_quantity,
        newQuantity: movement?.new_quantity,
        referenceType: movement?.reference_type,
        referenceId: movement?.reference_id,
        reason: movement?.reason,
        notes: movement?.notes,
        performedBy: movement?.performed_by,
        performer: movement?.performer,
        createdAt: movement?.created_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching stock movements:', error);
      throw error;
    }
  },

  // Get all suppliers
  async getSuppliers(activeOnly = false) {
    try {
      let query = supabase?.from('suppliers')?.select('*')?.order('name');

      if (activeOnly) {
        query = query?.eq('status', 'active');
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(supplier => ({
        id: supplier?.id,
        supplierCode: supplier?.supplier_code,
        companyName: supplier?.company_name,
        contactPerson: supplier?.contact_person,
        email: supplier?.email,
        phone: supplier?.phone,
        address: supplier?.address,
        city: supplier?.city,
        country: supplier?.country,
        paymentTerms: supplier?.payment_terms,
        leadTimeDays: supplier?.lead_time_days,
        rating: supplier?.rating,
        categoriesSupplied: supplier?.categories_supplied,
        isActive: supplier?.is_active,
        notes: supplier?.notes,
        metadata: supplier?.metadata,
        createdAt: supplier?.created_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error;
    }
  },

  // Create supplier
  async createSupplier(supplierData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { data: supplierCodeData } = await supabase?.rpc('generate_supplier_code');

      const { data, error } = await supabase?.from('suppliers')?.insert([{
          supplier_code: supplierCodeData,
          company_name: supplierData?.companyName,
          contact_person: supplierData?.contactPerson,
          email: supplierData?.email,
          phone: supplierData?.phone,
          address: supplierData?.address,
          city: supplierData?.city,
          country: supplierData?.country || 'DRC',
          payment_terms: supplierData?.paymentTerms,
          lead_time_days: supplierData?.leadTimeDays,
          rating: supplierData?.rating || 5.00,
          categories_supplied: supplierData?.categoriesSupplied || [],
          is_active: supplierData?.isActive !== false,
          notes: supplierData?.notes,
          metadata: supplierData?.metadata,
          created_by: user?.id,
        }])?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  },

  // Update supplier
  async updateSupplier(supplierId, updates) {
    try {
      const { data, error } = await supabase?.from('suppliers')?.update({
          company_name: updates?.companyName,
          contact_person: updates?.contactPerson,
          email: updates?.email,
          phone: updates?.phone,
          address: updates?.address,
          city: updates?.city,
          country: updates?.country,
          payment_terms: updates?.paymentTerms,
          lead_time_days: updates?.leadTimeDays,
          rating: updates?.rating,
          categories_supplied: updates?.categoriesSupplied,
          is_active: updates?.isActive,
          notes: updates?.notes,
          metadata: updates?.metadata,
        })?.eq('id', supplierId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  },

  // Alias methods for compatibility
  async createItem(itemData) {
    return this.createInventoryItem(itemData);
  },

  async updateItem(itemId, updates) {
    return this.updateInventoryItem(itemId, updates);
  },

  async deleteItem(itemId) {
    const { error } = await supabase?.from('inventory_items')?.delete()?.eq('id', itemId);
    if (error) throw error;
  },

  async deleteSupplier(supplierId) {
    const { error } = await supabase?.from('suppliers')?.delete()?.eq('id', supplierId);
    if (error) throw error;
  },

  async getAllItems(filters = {}) {
    return this.getInventoryItems(filters);
  },

  async getItemById(id) {
    try {
      const { data, error } = await supabase?.from('inventory_items')?.select(`
          *,
          supplier:suppliers(id, name, contact_person, phone, email)
        `)?.eq('id', id)?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching item by ID:', error);
      throw error;
    }
  },

  async getAllSuppliers(activeOnly = false) {
    return this.getSuppliers(activeOnly);
  },

  async getPendingAlerts() {
    try {
      const { data, error } = await supabase?.from('inventory_alerts')?.select(`
          *,
          inventory_item:inventory_items(id, item_code, name, current_quantity, minimum_quantity, location, supplier_id)
`)?.eq('status', 'pending')?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(alert => ({
        id: alert?.id,
        inventoryItemId: alert?.inventory_item_id,
        alertType: alert?.alert_type,
        message: alert?.message,
        status: alert?.status,
        acknowledgedAt: alert?.acknowledged_at,
        acknowledgedBy: alert?.acknowledged_by,
        createdAt: alert?.created_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching pending alerts:', error);
      throw error;
    }
  },

  async acknowledgeAlert(alertId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { data, error } = await supabase?.from('inventory_alerts')?.update({
        status: 'acknowledged',
        acknowledged_at: new Date()?.toISOString(),
        acknowledged_by: user?.id,
        })?.eq('id', alertId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      throw error;
    }
  },
};